import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Procedure from './../implant/Procedure';
import SETTING_KEYS from '../../constants/setting-keys';
import styles from './Implant.module.css';
import { getImplantById } from '../../services/apiService';
import LoadingRing from '../ui-components/LoadingRing';
import AuthContext from '../../store/auth-context';
import RIGHTS_KEYS from '../../constants/rights-keys';
import { getHistoryVersionsByImplantId } from '../../services/apiService';
import { useImplantContext } from '../../store/implant-context';
import Button, { ButtonType } from '../general-components/Button';
import BackgroundContainer from '../ui-components/BackgroundContainer';
import RegionalImplant from './RegionalImplant';
import accessLimitService from '../../services/api/accessLimitService';
import { resolveLastCheckedDate } from './implantDateUtils';

const Implant: React.FC<{
    isPreview?: boolean;
    id: string;
}> = ({ isPreview = false, id }) => {
    const { t } = useTranslation();
    const [info, setInfo] = useState<any>({});
    const [historyInfo, setHistoryInfo] = useState({});

    const [historyVersions, setHistoryVersions] = useState([]);
    const [selectedVersion, setSelectedVersion] = useState(null);

    const [conditionals, setConditionals] = useState<any[]>([]);
    const [conditionalsHistory, setConditionalHistory] = useState({});

    const [hasDepartment, setHasDepartment] = useState(false);

    const userContext = useContext(AuthContext);
    const {
        dispatchInfo,
        dispatchConditionals,
        dispatchRegionalInfos,
        regionalInfos,
    } = useImplantContext();

    const [isLoading, setIsLoading] = useState(true);
    const [blockedByLimit, setBlockedByLimit] = useState(false);
    const [blockedRetryAtUtc, setBlockedRetryAtUtc] = useState<string | null>(
        null
    );
    const [loadError, setLoadError] = useState<string | null>(null);

    // let validMriCode = null;
    // if (info.mriCode) {
    //     validMriCode = await mriCodeValidate(info.mriCode);
    // }

    const getData = async () => {
        if (!id) {
            setLoadError('Implant id is missing.');
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setBlockedByLimit(false);
        setBlockedRetryAtUtc(null);
        setLoadError(null);
        setInfo({});
        setConditionals([]);
        setHistoryVersions([]);
        setSelectedVersion(null);

        try {
            const preflightRes = await accessLimitService.preflightImplantLoad(id);
            if (preflightRes?.status === 429) {
                setBlockedByLimit(true);
                setBlockedRetryAtUtc(preflightRes?.data?.details?.retryAtUtc || null);
                return;
            }

            const implantData = await getImplantById(id);
            if (implantData?.error === 'RateLimitExceeded') {
                setBlockedByLimit(true);
                setBlockedRetryAtUtc(implantData?.details?.retryAtUtc ?? null);
                return;
            }

            if (!implantData?.info || typeof implantData.info !== 'object') {
                setLoadError('Failed to load implant data.');
                return;
            }

            const historyVersionsData = await getHistoryVersionsByImplantId(id);

            setInfo({
                [SETTING_KEYS.manufacturer]: implantData.Manufacturer,
                ...implantData.info,
            });


            dispatchInfo({
                type: 'BULK_UPDATE',
                payload: {
                    [SETTING_KEYS.manufacturer]: implantData.Manufacturer,
                    ...implantData.info,
                },
            });

            dispatchRegionalInfos({
                type: 'BULK_UPDATE',
                payload: implantData.info.regionalInfos || [],
            });

            const userInfo = await userContext?.getAllInfo();

            setHasDepartment(
                userInfo?.user?.department_id !== null &&
                    userInfo?.user?.department_id !== undefined
            );

            const loadedConditionals = Array.isArray(implantData.conditionals)
                ? implantData.conditionals
                : [];
            setConditionals(loadedConditionals);
            dispatchConditionals({
                type: 'BULK_UPDATE',
                payload: loadedConditionals,
            });
            setHistoryVersions(
                Array.isArray(historyVersionsData) ? historyVersionsData : []
            );
        } catch (err) {
            console.error('Failed to load implant page data:', err);
            setLoadError('Failed to load implant data.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getData();
    }, [id]);

    useEffect(() => {
        return () => {
            dispatchInfo({ type: 'RESET' });
            dispatchConditionals({ type: 'RESET' });
        };
    }, [dispatchInfo, dispatchConditionals]);

    const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedOption = historyVersions.find(
            (version) => version.id === event.target.value
        );

        setSelectedVersion(selectedOption);
        if (selectedOption) {
            // Update context with history version data
            const historyInfoData = {
                [SETTING_KEYS.manufacturer]: selectedOption.Manufacturer,
                ...selectedOption.info,
            };

            setConditionalHistory(selectedOption.conditionals);
            setHistoryInfo(historyInfoData);

            // Dispatch to context for RegionalImplant and other components
            dispatchInfo({
                type: 'BULK_UPDATE',
                payload: historyInfoData,
            });

            // Update regionalInfos in context
            if (selectedOption.info.regionalInfos) {
                dispatchRegionalInfos({
                    type: 'BULK_UPDATE',
                    payload: selectedOption.info.regionalInfos,
                });
            } else {
                // Reset to empty if no regionalInfos in history version
                dispatchRegionalInfos({
                    type: 'BULK_UPDATE',
                    payload: [],
                });
            }

            // Update conditionals in context
            if (selectedOption.conditionals) {
                // Ensure conditionals is an array
                const conditionalsArray = Array.isArray(
                    selectedOption.conditionals
                )
                    ? selectedOption.conditionals
                    : selectedOption.conditionals
                    ? [selectedOption.conditionals]
                    : [];

                dispatchConditionals({
                    type: 'BULK_UPDATE',
                    payload: conditionalsArray,
                });
            } else {
                // Reset conditionals if no conditionals in history version
                dispatchConditionals({
                    type: 'BULK_UPDATE',
                    payload: [],
                });
            }
        } else {
            // Reset to current implant data when no version is selected
            const currentInfo = {
                [SETTING_KEYS.manufacturer]: info[SETTING_KEYS.manufacturer],
                ...info,
            };

            dispatchInfo({
                type: 'BULK_UPDATE',
                payload: currentInfo,
            });

            if (info.regionalInfos) {
                dispatchRegionalInfos({
                    type: 'BULK_UPDATE',
                    payload: info.regionalInfos,
                });
            }

            if (conditionals) {
                dispatchConditionals({
                    type: 'BULK_UPDATE',
                    payload: conditionals,
                });
            }
        }
    };

    return (
        <>
            {isLoading && (
                <div className={styles.loadingwrapper}>
                    <LoadingRing size="large" />
                </div>
            )}
            {!isLoading && blockedByLimit && (
                <BackgroundContainer>
                    <p>Daily access limit reached for implant loads.</p>
                    {blockedRetryAtUtc && (
                        <p>Try again at {new Date(blockedRetryAtUtc).toUTCString()}.</p>
                    )}
                </BackgroundContainer>
            )}
            {!isLoading && !blockedByLimit && loadError && (
                <BackgroundContainer>
                    <p>{loadError}</p>
                </BackgroundContainer>
            )}
            {!isLoading &&
                !blockedByLimit &&
                !loadError &&
                regionalInfos.length > 0 && (
                <RegionalImplant
                    historyVersions={historyVersions}
                    selectedVersion={selectedVersion}
                    handleHistoryChange={handleChange}
                    isPreview={isPreview}
                />
            )}
            {!blockedByLimit && !loadError && regionalInfos.length === 0 && (
                <>
                    {isPreview &&
                        userContext.orRights([
                            RIGHTS_KEYS.manageAllImplants,
                        ]) && (
                            <BackgroundContainer>
                                {info.docDate && (
                                    <p>
                                        {t('implant.preview.documentDate', {
                                            date: info.docDate,
                                        })}
                                    </p>
                                )}
                                {resolveLastCheckedDate(info) && (
                                    <p>
                                        {t('implant.preview.lastChecked')}:{' '}
                                        {new Date(
                                            resolveLastCheckedDate(info) as any
                                        ).toLocaleDateString()}
                                    </p>
                                )}
                                <p>
                                    Source:{' '}
                                    {info[SETTING_KEYS.source] ? (
                                        <a
                                            href={info[SETTING_KEYS.source]}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            {info[SETTING_KEYS.source]}
                                        </a>
                                    ) : (
                                        'no link provided'
                                    )}
                                </p>
                                <div>
                                    <Button
                                        key={'Edit'}
                                        type={ButtonType.primary}
                                        onClick={() =>
                                            window.open(
                                                'https://www.google.com/search?q=' +
                                                    info[SETTING_KEYS.model] +
                                                    'mri safety',
                                                '_blank'
                                            )
                                        }
                                        className={styles['grey-btn']}
                                    >
                                        {t('implant.preview.googleInfo')}
                                    </Button>
                                </div>
                            </BackgroundContainer>
                        )}
                    {!isLoading && conditionals.length > 0 && (
                        <React.Fragment>
                            {selectedVersion ? (
                                <Procedure
                                    isPreview={isPreview}
                                    info={historyInfo}
                                    conditionals={conditionalsHistory}
                                    historyVersions={historyVersions}
                                    selectedVersion={selectedVersion}
                                    handleChange={handleChange}
                                    mriCode={info.mriCode}
                                    withDeptTools={
                                        !isPreview &&
                                        info._id &&
                                        userContext.orRights([
                                            RIGHTS_KEYS.deptTools,
                                        ]) &&
                                        hasDepartment
                                    }
                                />
                            ) : (
                                <Procedure
                                    isPreview={isPreview}
                                    info={info}
                                    conditionals={conditionals[0]}
                                    historyVersions={historyVersions}
                                    selectedVersion={selectedVersion}
                                    handleChange={handleChange}
                                    mriCode={info.mriCode}
                                    withDeptTools={
                                        !isPreview &&
                                        info._id &&
                                        userContext.orRights([
                                            RIGHTS_KEYS.deptTools,
                                        ]) &&
                                        hasDepartment
                                    }
                                />
                            )}
                        </React.Fragment>
                    )}
                </>
            )}
        </>
    );
};

export default Implant;
